/**
 * WorkspaceChat — the persistent Claude chat for a session.
 *
 * IMPORTANT: mounted ONCE by the shell (CustomCreativeSession) for the life
 * of the session and NOT re-mounted across stage changes. This is the ONLY
 * place chat / the LLM is ever invoked — nothing in the left column calls it.
 *
 * Claude never writes into a field. Each assistant reply exposes an explicit
 * "Copy to field" control; the operator chooses a target and the shell routes
 * the text into that field's editor (which the operator then owns).
 */
import React, { useEffect, useRef, useState } from 'react';

function CopyToField({ targets, onCopy }) {
  const [open, setOpen] = useState(false);
  if (!targets || targets.length === 0) return null;
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="text-xs text-slate-400 hover:text-emerald-400 transition-colors"
        data-testid="ccs-copy-to-field"
      >
        Copy to field ▸
      </button>
      {open && (
        <div className="absolute z-20 right-0 mt-1 w-40 bg-slate-900 border border-slate-700 rounded-lg shadow-xl">
          {targets.map((t) => (
            <button
              key={t.key}
              type="button"
              onMouseDown={() => { onCopy(t.key); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800 border-b border-slate-800 last:border-b-0"
              data-testid={`ccs-copy-target-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Message({ msg, copyTargets, onCopyToField, readOnly }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isUser
            ? 'bg-emerald-600/20 text-emerald-50 border border-emerald-700/40'
            : 'bg-slate-800 text-slate-200 border border-slate-700/60'
        }`}
        data-testid={`ccs-chat-msg-${msg.role}`}
      >
        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
        {!isUser && (
          <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t border-slate-700/50">
            <span className="text-[10px] text-slate-500">{msg.model || 'Claude'}</span>
            {!readOnly && (
              <CopyToField
                targets={copyTargets}
                onCopy={(key) => onCopyToField(key, msg.content)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorkspaceChat({
  sessionId,
  api,
  copyTargets = [],
  onCopyToField,
  readOnly = false,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [failedMessage, setFailedMessage] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(false);
    api.getMessages(sessionId)
      .then((res) => {
        if (!alive) return;
        setMessages((res.transcript || []).map((m) => ({
          role: m.role,
          content: m.content,
          ts: m.ts,
        })));
      })
      .catch(() => { if (alive) setLoadError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sessionId, api]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, thinking]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || thinking || readOnly) return;
    setFailedMessage(null);
    if (text === undefined) {
      // fresh send from composer — append user message + clear input
      setMessages((prev) => [...prev, { role: 'user', content: msg, ts: Date.now() }]);
      setInput('');
    }
    setThinking(true);
    try {
      const res = await api.sendChat(sessionId, msg);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.assistant, model: res.model, ts: Date.now() },
      ]);
    } catch (e) {
      setFailedMessage(msg);
    } finally {
      setThinking(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden" data-testid="ccs-workspace-chat">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Claude</h3>
        {readOnly && <span className="text-xs text-slate-500">Read-only</span>}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ccs-chat-list">
        {loading ? (
          <div className="space-y-3">
            <div className="h-10 bg-slate-800/60 rounded-lg animate-pulse w-2/3" />
            <div className="h-10 bg-slate-800/60 rounded-lg animate-pulse w-3/4 ml-auto" />
          </div>
        ) : loadError ? (
          <p className="text-xs text-amber-400">Could not load chat history.</p>
        ) : messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center">
            <p className="text-xs text-slate-500 max-w-[220px]">
              Ask Claude for help with hooks, angles or scripts. Claude never fills your
              fields — use "Copy to field" on a reply when you want to reuse its text.
            </p>
          </div>
        ) : (
          messages.map((m, i) => (
            <Message
              key={i}
              msg={m}
              copyTargets={copyTargets}
              onCopyToField={onCopyToField}
              readOnly={readOnly}
            />
          ))
        )}

        {thinking && (
          <div className="flex justify-start" data-testid="ccs-chat-thinking">
            <div className="bg-slate-800 border border-slate-700/60 rounded-lg px-3 py-2 text-sm text-slate-400 italic">
              Claude is thinking…
            </div>
          </div>
        )}

        {failedMessage && (
          <div className="text-xs text-rose-400 flex items-center gap-2" data-testid="ccs-chat-error">
            Message failed to send.
            <button
              type="button"
              onClick={() => send(failedMessage)}
              className="underline hover:text-rose-300"
              data-testid="ccs-chat-retry"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-slate-800 p-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={readOnly || thinking}
          placeholder={readOnly ? 'Session submitted — chat is read-only' : 'Message Claude…  (Enter to send, Shift+Enter for newline)'}
          rows={2}
          className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
          data-testid="ccs-chat-input"
        />
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => send()}
            disabled={readOnly || thinking || !input.trim()}
            className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors text-sm disabled:opacity-40"
            data-testid="ccs-chat-send"
          >
            {thinking ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
